// @flow
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import { setLoadingApp } from '../../actions/session';

import JoinConversationInput from './Input';
import JoinConversationMenu from './Menu';
import JoinConversationOption from './Option';
import JoinConversationPlaceholder from './Placeholder';
import JoinConversationSingleValue from './SingleValue';
import JoinConversationValueContainer from './ValueContainer';
import RoomAutoComplete from './RoomAutoComplete';

import { getSocketIfExist } from '../../utils/getSocket';
import { users } from '../../services/api/ava';
import { log } from '../../utils/log';
import * as segment from '../../utils/segment';
import { getSnack } from '../../utils/snackbar';
import { sendConnectionRequestMessage } from '../../utils/ws-v1';

const components = {
  Control: JoinConversationInput,
  Menu: JoinConversationMenu,
  DropdownIndicator: () => null,
  IndicatorSeparator: () => null,
  NoOptionsMessage: () => null,
  Option: JoinConversationOption,
  Placeholder: JoinConversationPlaceholder,
  ClearIndicator: () => null,
  SingleValue: JoinConversationSingleValue,
  ValueContainer: JoinConversationValueContainer,
};

interface Props {
  autoFocus: boolean;
  className?: string;
  roomComponents: Object;
  history: Object;
  user: Object;
  setLoadingApp: Function;
}

const SearchAvaRoomInput = ({ autoFocus, className, roomComponents, history, user, setLoadingApp }: Props) => {
  const { t } = useTranslation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [avaName, setAvaName] = useState('&');
  const [avaNameRequested, setAvaNameRequested] = useState(undefined);
  const [requestedMeta, setRequestedMeta] = useState(undefined);
  const [askingUsername, setAskingUsername] = useState(false);
  const [waitForConnectResponse, setWaitForConnectResponse] = useState(false);

  const {
    location: { pathname },
  } = history;

  const onInputChange = value => setAvaName(value);

  const onConnectResponse = (data: any, name?: string, meta: Object, stopListening: Function) => {
    setWaitForConnectResponse(false);

    if (data.value) return;

    // There was a problem, stop listening to socket
    stopListening();
    if (data.reason === 'mustUpdateUsername') {
      setAskingUsername(true);
      setRequestedMeta(meta);
      setAvaNameRequested(name);
      return;
    }

    getSnack({ enqueueSnackbar, closeSnackbar })(t(`snackbar.request.${data.reason || 'unknownError'}`), {
      variant: 'error',
    });
  };

  const handleAvaName = async (avaNameArg: string, meta?: Object) => {
    if (user.avaName === avaNameArg) {
      alert('You cannot join your own conversation');
      return;
    }
    let avaName = avaNameArg;
    if (!avaName) {
      avaName = avaNameRequested;
    }

    if (!meta) {
      // eslint-disable-next-line
      meta = requestedMeta;
      setRequestedMeta(undefined);
    }

    if (user.username === 'web guest' && !meta && askingUsername) {
      setAskingUsername(false);
      return;
    }

    setWaitForConnectResponse(true);
    setAskingUsername(false);

    const socket = getSocketIfExist();
    const id = sendConnectionRequestMessage(socket, 'default', { avaName }, meta);
    let gotConnectionResponse = false;

    const handleConnectResponse = message => {
      const data = JSON.parse(message.data);

      const stopListening = () => {
        if (socket) {
          socket.removeEventListener('message', handleConnectResponse);
        }
        setLoadingApp(false);
      };

      if (data.type === 'connection-response' && data.id === id) {
        log('websocketMessage', 'info', {
          message: data,
          direction: 'b2m',
        });
        gotConnectionResponse = true;
        onConnectResponse(data, avaName, meta, stopListening);
      } else if (data.type === 'room-status' && gotConnectionResponse) {
        stopListening();

        // This user just joined a conversation
        segment.track('Joined Conversation', {
          HostAvaName: data.host.avaName,
          HostUserId: data.host.avaId,
          'Join Method': meta && meta.viaBranch ? 'Share Link' : 'Connect Request',
        });

        const metricsToUpdate = {
          'Total Joins': 1,
          'Total Invites Connected Count': meta && meta.viaBranch ? 1 : 0,
        };

        users.updateJoinMetrics({
          avaId: user.avaId,
          firebaseAuthUID: user.firebaseAuthUID || user.id,
          joinMetrics: metricsToUpdate,
        });
      }
    };

    if (socket) {
      socket.addEventListener('message', handleConnectResponse);
    }
  };

  const onAvaName = async (name: string) => {
    if (!name || name.length < 3) return;

    setAvaName(name);
    if (typeof name !== 'string') return;

    setLoadingApp(true);

    if (!pathname.includes('/web/transcript')) {
      history.push('/web/transcript');
    }

    handleAvaName(name.slice(1));
  };

  return (
    <div className={className}>
      <RoomAutoComplete
        autoFocus={autoFocus}
        components={{ ...components, ...roomComponents }}
        onAvaName={onAvaName}
        onChange={onInputChange}
      />
    </div>
  );
};

const mapStateToProps = ({ scribeSession: { captioner } }) => ({
  user: captioner,
});

const mapDispatchToProps = {
  setLoadingApp,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SearchAvaRoomInput));
